import type { FC } from "react";
import { Link } from "react-router-dom";
import { Copy, RefreshCw, Share2 } from "lucide-react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common";
import { InputV3 } from "components/common/input/InputV3";
import { Skeleton } from "components/common/skeleton";
import { PATH_DASHBOARD } from "config/urls";
import { useReferralInviteDialogData } from "hooks/referral/useReferralInviteDialogData";
import type { ReferralInviteDialogProps } from "./ReferralInviteDialog.types";

export const ReferralInviteDialog: FC<ReferralInviteDialogProps> = ({ isOpen, onClose }) => {
  const { codeQuery, shareUrl, code, share } = useReferralInviteDialogData({ enabled: isOpen });

  const payload = codeQuery.data;
  const loadFailed = codeQuery.isError && !payload;
  const canShare = Boolean(shareUrl || code);
  const displayLink = shareUrl || code || "—";

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="max-w-lg">
      <ModalHeader
        title="Invite people to RivetAI"
        subtitle="Share your referral link. When someone signs up and buys their first project license, eligible accounts can earn bonus project licenses."
        onClose={onClose}
      />
      <ModalBody showBorders={false}>
        {codeQuery.isLoading && !payload ? (
          <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading referral link">
            <Skeleton className="h-12 w-full rounded-md" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-9 w-20 rounded-lg" />
              <Skeleton className="h-9 w-24 rounded-lg" />
              <Skeleton className="h-9 w-24 rounded-lg" />
            </div>
          </div>
        ) : loadFailed ? (
          <div className="flex flex-col gap-4 rounded-md border border-danger/25 bg-danger/5 p-4">
            <p className="text-sm text-danger/90">
              We couldn&apos;t load your referral link. Check your connection and try again.
            </p>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void codeQuery.refetch()}
              className="w-fit gap-2"
            >
              <RefreshCw className="w-4 h-4 shrink-0" aria-hidden />
              Retry
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <InputV3
                label="Your signup link"
                readOnly
                aria-readonly="true"
                value={displayLink}
                title={
                  shareUrl ||
                  (code ? `${window.location.origin}/register?ref=${code}` : displayLink)
                }
                inputClassName="cursor-default font-mono truncate"
                endAdornment={
                  <Button
                    type="button"
                    color="gray"
                    size="icon"
                    onClick={share.copyLink}
                    disabled={!canShare}
                    className="h-7 w-7"
                    aria-label="Copy referral signup link to clipboard"
                  >
                    <Copy aria-hidden />
                  </Button>
                }
              />
              {!shareUrl && code ? (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Full signup URL uses this site&apos;s address with your code in the link.
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghostSecondary"
                onClick={share.shareEmail}
                disabled={!canShare}
                className="gap-2"
                aria-label="Share referral link by email"
              >
                <Share2 className="w-4 h-4 shrink-0" aria-hidden />
                Email
              </Button>
              <Button
                type="button"
                variant="ghostSecondary"
                onClick={share.shareLinkedIn}
                disabled={!canShare}
                aria-label="Share referral link on LinkedIn"
              >
                LinkedIn
              </Button>
              <Button
                type="button"
                variant="ghostSecondary"
                onClick={share.shareTwitter}
                disabled={!canShare}
                aria-label="Share referral link on X"
              >
                X / Twitter
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Want a custom link ending or full history?{" "}
              <Link
                to={PATH_DASHBOARD.referrals}
                className="font-medium text-hue-violet underline decoration-hue-violet/50 underline-offset-2 hover:text-hue-violet/80"
                onClick={onClose}
              >
                Open referral settings
              </Link>
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-end">
        <Button type="button" variant="ghostSecondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
